import { useContext, useState } from "react";
import { DatabaseSearchContext, Wrapper } from "nystem-components";
import app from "nystem";

const getValue = (item, id) =>
  id.split(".").reduce((value, part) => value && value[part], item);

const ViewListGraph = ({ model }) => {
  const { search } = useContext(DatabaseSearchContext);
  const fields = model.fields || [];
  const dateField = model.dateField || "_crdate";
  const [selected, setSelected] = useState(fields[0]?.id);

  if (!fields.length) return null;

  const field = fields.find(({ id }) => id === selected) || fields[0];
  const data = ((search && search.data) || [])
    .map((item) => ({
      date: new Date(getValue(item, dateField)),
      value: parseInt(getValue(item, field.id) || 0, 10),
    }))
    .filter(
      ({ date, value }) => !Number.isNaN(date.getTime()) && !Number.isNaN(value),
    )
    .sort((a, b) => a.date - b.date);

  const values = data.map(({ value }) => value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const width = parseInt(model.width || 640, 10);
  const height = parseInt(model.height || 220, 10);
  const padding = { top: 18, right: 20, bottom: 34, left: 56 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const points = data.map(({ value }, index) => {
    const x =
      padding.left +
      (data.length < 2
        ? plotWidth / 2
        : (plotWidth * index) / (data.length - 1));
    const y = padding.top + plotHeight - ((value - min) / range) * plotHeight;
    return { x, y, value };
  });

  return (
    <Wrapper className={model.className || ["mb-4"]}>
      <Wrapper className={["flex", "items-center", "mb-2"]}>
        <Wrapper className={["font-semibold", "flex-grow"]}>
          {app.t(model.text || "Graph")}
        </Wrapper>
        <select
          className="form-select w-48"
          value={selected || ""}
          onChange={(event) => setSelected(event.target.value)}
        >
          {fields.map(({ id, text }) => (
            <option key={id} value={id}>
              {app.t(text || id)}
            </option>
          ))}
        </select>
      </Wrapper>
      {data.length < 2 ? (
        <Wrapper className={["p-4", "text-gray-500"]}>
          {app.t(model.emptyText || "Not enough data to draw graph.")}
        </Wrapper>
      ) : (
        <svg
          className="block max-w-full"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
        >
          <line
            x1={padding.left}
            x2={padding.left}
            y1={padding.top}
            y2={padding.top + plotHeight}
            stroke="currentColor"
            opacity="0.35"
          />
          <line
            x1={padding.left}
            x2={width - padding.right}
            y1={padding.top + plotHeight}
            y2={padding.top + plotHeight}
            stroke="currentColor"
            opacity="0.35"
          />
          <text x="0" y={padding.top + 4} className="fill-current text-xs">
            {max}
          </text>
          <text
            x="0"
            y={padding.top + plotHeight}
            className="fill-current text-xs"
          >
            {min}
          </text>
          <polyline
            fill="none"
            points={points.map(({ x, y }) => `${x},${y}`).join(" ")}
            stroke="currentColor"
            strokeWidth="2"
          />
          {points.map(({ x, y, value }, index) => (
            <circle key={index} cx={x} cy={y} r="3">
              <title>{value}</title>
            </circle>
          ))}
          <text
            x={padding.left}
            y={height - 8}
            className="fill-current text-xs"
          >
            {data[0].date.toLocaleDateString()}
          </text>
          <text
            x={width - padding.right}
            y={height - 8}
            textAnchor="end"
            className="fill-current text-xs"
          >
            {data[data.length - 1].date.toLocaleDateString()}
          </text>
        </svg>
      )}
    </Wrapper>
  );
};

export default ViewListGraph;
